/**
 * Graphing V2 — landing / marketing page.
 *
 * Blueprint / engineering-console aesthetic: near-black ink + blueprint dot-grid,
 * an electric-lime signal accent, technical mono labels, and the bright diagram
 * previews as the hero colour. Lands here by default; the primary CTA routes to
 * the dashboard (the user's projects); showcase cards open the editor to explore.
 */

import { useEffect, useRef } from 'react'
import type { DemoExample } from '../examples'
import './DemoGallery.css'

interface DemoGalleryProps {
  examples: DemoExample[]
  onOpen: (example: DemoExample) => void
  onOpenDashboard: () => void
}

const TYPE_LABEL: Record<string, string> = {
  architecture: 'ARCHITECTURE',
  erd: 'ERD',
  class: 'UML CLASS',
  state: 'STATE MACHINE',
  c4: 'C4 MODEL',
  sequence: 'SEQUENCE',
  gantt: 'GANTT',
  flowchart: 'FLOWCHART',
}

const CAPABILITIES = [
  ['8', 'diagram types', 'architecture · ERD · class · state · C4 · sequence · gantt · flow'],
  ['AI', 'native authoring', 'describe it in Mermaid or via the MCP — it builds itself'],
  ['↔', 'live collaboration', 'multiplayer rooms, multi-page canvases, real-time sync'],
  ['⤓', 'export anywhere', 'PNG · SVG · JSON, AI-friendly or full fidelity'],
]

export default function DemoGallery({ examples, onOpen, onOpenDashboard }: DemoGalleryProps) {
  const featured = examples.find((e) => e.id === 'architecture') ?? examples[0]
  const rest = examples.filter((e) => e !== featured)
  const rootRef = useRef<HTMLDivElement>(null)

  // Staggered reveal on load.
  useEffect(() => {
    const els = rootRef.current?.querySelectorAll<HTMLElement>('[data-reveal]')
    els?.forEach((el, i) => {
      el.style.setProperty('--d', `${Math.min(i * 60, 600)}ms`)
    })
  }, [])

  return (
    <div className="gv2" ref={rootRef}>
      <div className="gv2-grid-bg" aria-hidden />
      <div className="gv2-glow" aria-hidden />

      {/* Top bar */}
      <nav className="gv2-nav" data-reveal>
        <div className="gv2-wordmark">
          <span className="gv2-mark" aria-hidden>◳</span>
          Graphing<span className="gv2-mark-accent">V2</span>
        </div>
        <div className="gv2-nav-right">
          <span className="gv2-nav-tag">v2 · diagram engine</span>
          <button className="gv2-nav-btn" onClick={onOpenDashboard}>Open dashboard →</button>
        </div>
      </nav>

      {/* Hero */}
      <header className="gv2-hero">
        <div className="gv2-kicker" data-reveal>// DIAGRAMS, AUTHORED BY HUMANS &amp; AI</div>
        <h1 className="gv2-title" data-reveal>
          The diagram engine<br />
          that <span className="gv2-title-accent">draws itself.</span>
        </h1>
        <p className="gv2-lede" data-reveal>
          One product — <strong>ShopSphere</strong> — modeled eight ways. Architecture, schema, UML,
          state, C4, sequence, and timeline. Describe it in Mermaid or let an agent build it; every
          example below is live, editable, and pixel-rendered.
        </p>
        <div className="gv2-cta" data-reveal>
          <button className="gv2-btn-primary" onClick={onOpenDashboard}>Open your dashboard →</button>
          <a className="gv2-btn-ghost" href="#showcase">Browse the showcase</a>
        </div>

        <div className="gv2-caps" data-reveal>
          {CAPABILITIES.map(([big, label, sub]) => (
            <div className="gv2-cap" key={label}>
              <span className="gv2-cap-big">{big}</span>
              <div className="gv2-cap-text">
                <span className="gv2-cap-label">{label}</span>
                <span className="gv2-cap-sub">{sub}</span>
              </div>
            </div>
          ))}
        </div>
      </header>

      {/* Showcase */}
      <main id="showcase" className="gv2-showcase">
        <div className="gv2-section-head" data-reveal>
          <h2 className="gv2-section-title">The ShopSphere showcase</h2>
          <span className="gv2-section-meta">{examples.length} preserved examples · click to explore</span>
        </div>

        {/* Featured */}
        {featured && (
          <button className="gv2-featured" data-reveal onClick={() => onOpen(featured)} title={`Open "${featured.title}"`}>
            <div className="gv2-featured-thumb">
              <img src={featured.thumbnail} alt={featured.title} />
            </div>
            <div className="gv2-featured-body">
              <span className="gv2-type">{TYPE_LABEL[featured.diagramType] ?? featured.diagramType}</span>
              <h3 className="gv2-featured-title">{featured.title}</h3>
              <p className="gv2-featured-desc">{featured.description}</p>
              <span className="gv2-open">Open in editor →</span>
            </div>
          </button>
        )}

        {/* Grid */}
        <div className="gv2-cards">
          {rest.map((ex) => (
            <button key={ex.id} className="gv2-card" data-reveal onClick={() => onOpen(ex)} title={`Open "${ex.title}"`}>
              <div className="gv2-card-thumb">
                <img src={ex.thumbnail} alt={ex.title} loading="lazy" />
                <span className="gv2-type">{TYPE_LABEL[ex.diagramType] ?? ex.diagramType}</span>
              </div>
              <div className="gv2-card-body">
                <h3 className="gv2-card-title">{ex.title}</h3>
                <p className="gv2-card-desc">{ex.description}</p>
                <span className="gv2-open">Open →</span>
              </div>
            </button>
          ))}
        </div>
      </main>

      <footer className="gv2-footer" data-reveal>
        <span className="gv2-mark" aria-hidden>◳</span>
        <p>
          <strong>Graphing V2</strong> · examples are preserved &amp; regenerated from source ·
          built with the diagram-editor library.
        </p>
      </footer>
    </div>
  )
}
